import React, { useMemo } from 'react';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import { Formik, Field } from 'formik';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import ErrorBoundary from 'shared/components/error-boundary';
import OfficeField from 'shared/components/form/fields/office';
import * as Yup from 'yup';

const validationSchema = Yup.object().shape({
  office: Yup.object()
    .shape({})
    .nullable()
    .required()
});

export default function PracticeFoundCard({
  contactAdmin,
  createNewOffice,
  error,
  loading,
  offices,
  officesWithUsers,
  useExistingOffice
}) {
  // Combine all offices into a single list
  const allOffices = useMemo(() => {
    const combined = [];
    if (offices?.length > 0) combined.push(...offices);
    if (officesWithUsers?.length > 0) {
      // Add officesWithUsers that aren't already in offices, marking them as having active admins
      const officeIds = new Set(offices?.map(o => o.id) || []);
      for (const o of officesWithUsers) {
        if (!officeIds.has(o.id)) {
          combined.push({ ...o, hasActiveAdmin: true });
        }
      }
    }
    return combined;
  }, [offices, officesWithUsers]);

  const initialValues = allOffices.length === 1 ? { office: allOffices[0] } : { office: '' };
  const isInitialValid = validationSchema.isValidSync(initialValues);

  return (
    <Card>
      <Card.Header>Existing Practice Found</Card.Header>
      <Card.Body>
        <ErrorBoundary>
          <Formik
            displayName={'SelectPractice'}
            onSubmit={values => {
              if (values.office?.hasActiveAdmin) {
                contactAdmin(values.office);
              } else {
                useExistingOffice(values.office);
              }
            }}
            isInitialValid={isInitialValid}
            initialValues={initialValues}
            validationSchema={validationSchema}
            enableReinitialize={true}
            validateOnChange={true}
            validateOnBlur={true}
            render={({ submitForm, isValid, values }) => (
              <form>
                <h3>Select a Practice</h3>
                <p>
                  We found practices associated with your provider. Please select a practice to join
                  or register a new one.
                </p>
                <Row className="form-group">
                  <Col md={8}>
                    <Field
                      name="office"
                      component={OfficeField}
                      placeholder="Please select..."
                      options={allOffices}
                      defaultValue={''}
                      disabled={loading}
                      label="Select Practice"
                    />
                  </Col>
                </Row>
                <Row className="form-group">
                  <Col md={9}>
                    {error && (
                      <>
                        <i className="fa fa-exclamation-triangle fa-fw has-error" />
                        {error}
                      </>
                    )}
                  </Col>
                  <Col md={3}>
                    <div className="float-end">
                      {values.office && (
                        <Button
                          className="btn btn-primary btn--fw"
                          disabled={!isValid || loading}
                          onClick={submitForm}
                        >
                          {values.office?.hasActiveAdmin ? 'Request Access' : 'Register'}
                        </Button>
                      )}
                    </div>
                  </Col>
                </Row>

                <Row className="form-group">
                  <Col sm={2}>
                    <hr />
                  </Col>
                  <Col sm={1}>Or</Col>
                  <Col sm={9}>
                    <hr />
                  </Col>
                </Row>

                <Row className="form-group">
                  <Col md={12}>
                    None of the above practices are my practice. I want to create a new practice
                    not listed above.
                  </Col>
                </Row>

                <Row className="form-group">
                  <Col md={12}>
                    <div className="float-end">
                      <Button className="btn btn-default btn--fw" onClick={createNewOffice}>
                        Create Practice
                      </Button>
                    </div>
                  </Col>
                </Row>
              </form>
            )}
          />
        </ErrorBoundary>
      </Card.Body>
    </Card>
  );
}

